import type { Campaign, ICampaignVariantVersion } from '~/modules/campaign/types';
import type { IPickPageItem } from '~/modules/modal-pick-page';
import type { IVariantPageInfo } from '../utils';

import {
  groupBaseIDsByPickPageType,
  toVariantUrlRedirectPage,
  withVariantUrlRedirectLivePage,
  withVariantUrlRedirectPageInfo,
} from '../utils';
import { useVariantUrlRedirectLivePages } from '../hooks/useVariantUrlRedirectLivePages';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CAMPAIGN_TYPE } from '~/constants/campaign';

interface ICampaignUrlRedirectPageContext {
  pageByVariantId: Record<string, IPickPageItem>;
  handleSetVariantPageInfo: (variantId: string, pageInfo: IVariantPageInfo, baseID: string) => void;
}

interface IPickedPageInfo extends IVariantPageInfo {
  baseID: string;
}

const CampaignUrlRedirectPageContext = createContext<ICampaignUrlRedirectPageContext | null>(null);

export const useCampaignUrlRedirectPageContext = (): ICampaignUrlRedirectPageContext => {
  const context = useContext(CampaignUrlRedirectPageContext);
  if (!context) {
    throw new Error(
      'useCampaignUrlRedirectPageContext must be used within a CampaignUrlRedirectPageProvider',
    );
  }

  return context;
};

/**
 * The name a listing row shows for the page its variant points at. Rows also render on screens that
 * never mount the provider, and there the title the variant stored is all they can show.
 */
export const useCampaignUrlRedirectPageTitle = (variantId?: string): string | undefined => {
  const context = useContext(CampaignUrlRedirectPageContext);

  return variantId ? context?.pageByVariantId[variantId]?.title : undefined;
};

interface IProps {
  children: React.ReactNode;
  /** One campaign on a detail screen, a whole page of them on the listing. */
  campaigns?: Array<Campaign | undefined | null>;
  /** Version settings of the campaign being viewed, whose snapshot outranks what a variant holds today. */
  campaignVariants?: Record<string, ICampaignVariantVersion>;
}

export const CampaignUrlRedirectPageProvider: React.FC<IProps> = ({
  children,
  campaigns,
  campaignVariants,
}) => {
  const [pickedPageInfoByVariantId, setPickedPageInfoByVariantId] = useState<
    Record<string, IPickedPageInfo>
  >({});

  const storedPageByVariantId = useMemo(() => {
    const map: Record<string, IPickPageItem> = {};

    campaigns?.forEach((campaign) => {
      if (campaign?.type !== CAMPAIGN_TYPE.URL) return;

      campaign.variants?.forEach((variant) => {
        if (!variant) return;

        const versionVariant = campaignVariants?.[variant.id];
        const page = toVariantUrlRedirectPage({
          variant,
          versionDestination: versionVariant?.destination,
          versionProduct: versionVariant?.product,
        });
        if (!page) return;

        map[variant.id] = page;
      });
    });

    return map;
  }, [campaigns, campaignVariants]);

  // A URL test sends visitors to any storefront page, and each kind of page lives behind its own
  // query, so what a variant points at is only ever read through the group its type falls in.
  const baseIDGroups = useMemo(
    () => groupBaseIDsByPickPageType(Object.values(storedPageByVariantId)),
    [storedPageByVariantId],
  );
  // Only the live title and artwork are read here, so no theme templates are handed in.
  const { livePageByBaseID } = useVariantUrlRedirectLivePages(baseIDGroups);

  const handleSetVariantPageInfo = useCallback(
    (variantId: string, pageInfo: IVariantPageInfo, baseID: string) => {
      setPickedPageInfoByVariantId((current) => ({
        ...current,
        [variantId]: { ...pageInfo, baseID },
      }));
    },
    [],
  );

  const pageByVariantId = useMemo(() => {
    const map: Record<string, IPickPageItem> = {};

    Object.entries(storedPageByVariantId).forEach(([variantId, storedPage]) => {
      const pickedInfo = pickedPageInfoByVariantId[variantId];
      const fallbackInfo = pickedInfo?.baseID === storedPage.baseID ? pickedInfo : undefined;

      // The picked info covers the gap between choosing a page and its lookup landing.
      map[variantId] = withVariantUrlRedirectLivePage(
        withVariantUrlRedirectPageInfo(storedPage, fallbackInfo),
        livePageByBaseID[storedPage.baseID],
      );
    });

    return map;
  }, [storedPageByVariantId, pickedPageInfoByVariantId, livePageByBaseID]);

  const value = useMemo<ICampaignUrlRedirectPageContext>(
    () => ({ pageByVariantId, handleSetVariantPageInfo }),
    [pageByVariantId, handleSetVariantPageInfo],
  );

  return (
    <CampaignUrlRedirectPageContext.Provider value={value}>
      {children}
    </CampaignUrlRedirectPageContext.Provider>
  );
};
